//* Import necessary modules
import { useState, useCallback } from "react";

//* Import necessary modules from the react-query library
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//? Import the necessary modules from the questionsTestAPI file
import { questionTestAPI } from "../questionsTest/questionTestAPI";

//? Import the necessary types from the types file
import {
  GetQuestionTestsResponse,
  CreateQuestionTestInput,
} from "../questionsTest/types/types";

//* Import the Pagination type from the services/stores/types file
import { Pagination } from "../../services/stores/types";

export const useQuestionTest = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "questionTests",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } =
    useQuery<GetQuestionTestsResponse>({
      queryKey,
      queryFn: () =>
        questionTestAPI.getAll(
          search,
          pagination.currentPage,
          token,
          pagination.itemsPerPage
        ),
      staleTime: 5 * 60 * 1000,
    });

  const questionTests = data?.questionTests || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createMutation = useMutation({
    mutationFn: (input: CreateQuestionTestInput) =>
      questionTestAPI.create(input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionTests"] });
    },
  });

  // Func for deleting multiple questionTest relations
  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      // Loop through the array of IDs and delete each one
      for (const id of ids) {
        await questionTestAPI.delete([id], token);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionTests"] });
    },
  });

  return {
    questionTests,
    loading: isLoading || isFetching,
    error: isError ? (error as Error)?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createRelations: createMutation.mutate,
    deleteRelations: deleteMutation.mutate, 
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
    changePage,
  };
};
